import {
  commaSeparatedString,
  currencyString,
} from '@calculations/decimal/format';
import { getSmallestLeadTime } from '@calculations/lead-time/smallest-lead-time';
import { snapRoundCustom } from '@calculations/round/decimal';
import {
  eaches,
  perQuantityToSiteUnits,
} from '@calculations/utilities/selling-uom';
import { UserWithSettingsResponse } from '@lib/responses/user';
import {
  LeadTimeStrategy,
  SellingUnitOfMeasure,
} from '@snap-types/models/enums';
import { Decimal } from '@snap-types/types/decimal';

import { PDFProviderType } from '../../components/PDF/PDFProvider';
import { QuoteBuilderStateType } from '../../types/state';

/**
 * The quote document has always shown quantities in eaches, whatever the site
 * sells in — the "Price/M" heading is what tells the reader the price is per
 * thousand. State is now in eaches, so this renders it directly.
 *
 * The service-charge special case is gone with it: those quantities count
 * services and were already unconverted, so both cases are now the same.
 */
function formatQuantity(quantity: Decimal) {
  return commaSeparatedString(snapRoundCustom(quantity, 0));
}

/**
 * Prices, unlike quantities, are shown per selling unit — the heading reads
 * "Price/M" on a thousands site — so they do convert.
 */
function formatUnitPrice(
  unitPrice: Decimal,
  sellingUnitOfMeasure: SellingUnitOfMeasure,
  isServiceCharge: boolean,
) {
  if (isServiceCharge) {
    return currencyString(unitPrice);
  }

  return currencyString(
    perQuantityToSiteUnits(eaches(unitPrice), {
      sellingUnitOfMeasure: sellingUnitOfMeasure,
    }),
  );
}

// @TODO Validate here;
export function mergeFormAndState(
  state: QuoteBuilderStateType,
  user: UserWithSettingsResponse,
  sellingUnitOfMeasure: SellingUnitOfMeasure,
  formattedQuoteTotal: string | null,
): PDFProviderType {
  const pricingUnit =
    sellingUnitOfMeasure === SellingUnitOfMeasure.EA ? 'Price Each' : 'Price/M';

  return {
    customer: {
      id: state.customerId,
      attentionName: state.contactName,
      name: state.name,
      address1: state.address1,
      address2: state.address2,
      city: state.city,
      state: state.state,
      postalCode: state.postalCode,
      countryCode: state.countryCode,
      email: state.contactEmail,
      phone: state.contactPhone,
      termsNetDays: state.termsNetDays,
      salesRepId: state.salesRepId,
      salesRepName: state.salesRepName,
      salesRepEmail: state.salesRepEmail,
      salesRepPhone: state.salesRepPhone,
      customerQuoteId: state.customerQuoteId,
    },
    daysUntilExpiration: state.daysUntilQuoteExpires,
    specs: state.specs,
    quoteId: state.quoteId,
    leadTimeFormat: state.leadTimeFormat,
    formattedQuoteTotal,
    quoteLines: state.quoteLineUuids.map((id, index) => {
      const isServiceCharge = !!state.quoteLines[id].serviceChargeId;
      const quoteLine = state.quoteLines[id];

      const part = quoteLine.finishedPartId
        ? {
            partId: quoteLine.finishedPartId,
            erpDB: quoteLine.erpDB,
            createdBySnap: quoteLine.partCreatedBySnap,
            customerPartId: quoteLine.customerPartId,
            description: quoteLine.description,
            drawingId: quoteLine.snapWorkOrder?.drawingId,
            drawingRevision: quoteLine.snapWorkOrder?.drawingRevisionNo,
            partSpecs: quoteLine.partSpecs,
          }
        : null;

      const serviceCharge = quoteLine.serviceChargeId
        ? {
            id: quoteLine.serviceChargeId,
            description: quoteLine.serviceChargeDescription,
          }
        : null;

      return {
        estQtyOnHand: quoteLine.estQtyOnHand,
        id,
        leadTimeWeeks: getSmallestLeadTime(
          Object.values(quoteLine.quotePrices),
        ),
        leadTimeNotes: quoteLine.leadTimeNotes,
        isQuantityAwareLeadTime:
          quoteLine.leadTimeConfig?.leadTimeType ===
          LeadTimeStrategy.QUANTITY_AWARE,
        lineNumber: index + 1,
        specs: quoteLine.specs,
        part,
        partOptions:
          quoteLine.productOptionsUuids?.map((uuid) => {
            const option = quoteLine.productOptions[uuid];

            return {
              id: uuid,
              name: option.optionName,
              groupName: option.groupName,
            };
          }) ?? [],
        quotePrices: quoteLine.quotePriceUuids.map((id) => {
          const quotePrice = quoteLine.quotePrices[id];

          return {
            id,
            quantity: formatQuantity(quotePrice.quantity),
            unitPrice: formatUnitPrice(
              quotePrice.unitPrice,
              sellingUnitOfMeasure,
              isServiceCharge,
            ),
            total: currencyString(quotePrice.totalPrice),
            leadTimeWeeks: quotePrice.leadTimeWeeks,
          };
        }),
        serviceCharge,
      };
    }),
    user: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    },
    csrName: state.csrName,
    csrEmail: state.csrEmail,
    pricingUnit,
  };
}
